import React, { useMemo } from 'react';
import { Redirect, useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faIndustry, faFileAlt } from '@fortawesome/free-solid-svg-icons';
import { useBranding } from '../hooks/useBranding';
import logo from 'assets/icons/logo.png';

const buildPricingPlans = (t) => [
  {
    id: 'operator',
    title: t('app:pricing.operator.title', 'Operator Plan'),
    icon: faIndustry,
    price: '£500',
    cadence: t('app:pricing.operator.cadence', 'per factory / year'),
    description: t(
      'app:pricing.operator.description',
      'Ideal for manufacturers and operators calculating CBAM emissions per installation.',
    ),
    bullets: [
      t('app:pricing.operator.bullet1', 'Factory-level emissions monitoring and reporting'),
      t('app:pricing.operator.bullet2', 'Digital diagrams for installations and processes'),
      t('app:pricing.operator.bullet3', 'Collaboration tools for suppliers and clients'),
    ],
    badge: t('app:pricing.operator.badge', 'Best for Operators'),
    targetType: 'operator',
  },
  {
    id: 'declarant',
    title: t('app:pricing.declarant.title', 'Declarant Plan'),
    icon: faFileAlt,
    price: '€2,500',
    cadence: t('app:pricing.declarant.cadence', 'per entity / year'),
    description: t(
      'app:pricing.declarant.description',
      'Designed for importers who need reliable CBAM reporting across multiple entities.',
    ),
    bullets: [
      t('app:pricing.declarant.bullet1', 'Full access to declarant workflows and reporting'),
      t('app:pricing.declarant.bullet2', 'Advanced analytics for imports and CBAM exposure'),
      t('app:pricing.declarant.bullet3', 'Priority support for onboarding and configuration'),
    ],
    badge: t('app:pricing.declarant.badge', 'Best for Importers'),
    targetType: 'declarant',
  },
];

const PricingPlanCard = ({ plan, onSelect }) => {
  const { t } = useTranslation('app');
  return (
    <button
      type="button"
      onClick={() => onSelect(plan)}
      className="w-full p-8 bg-white rounded-lg shadow-md hover:shadow-lg transition-all duration-300 hover:scale-105 focus:outline-none focus:ring-2 focus:ring-primaryColor focus:ring-offset-2 text-left"
    >
      <div className="flex flex-col h-full">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-4">
            <div className="flex items-center justify-center w-12 h-12 rounded-full bg-primaryColor/10 text-primaryColor">
              <FontAwesomeIcon
                icon={plan.icon}
                size="lg"
              />
            </div>
            <h3 className="text-2xl font-semibold text-gray-900">{plan.title}</h3>
          </div>
          {plan.badge && (
            <span className="px-3 py-1 rounded-full text-xs font-semibold bg-primaryColor/10 text-primaryColor">
              {plan.badge}
            </span>
          )}
        </div>

        <div className="mb-4">
          <div className="text-3xl font-bold text-gray-900">{plan.price}</div>
          <div className="text-sm text-gray-500">{plan.cadence}</div>
        </div>

        <p className="text-gray-600 mb-4">{plan.description}</p>

        <ul className="space-y-2 mb-6 text-gray-600 text-sm">
          {plan.bullets.map((item, idx) => (
            <li
              key={idx}
              className="flex items-start gap-2"
            >
              <span className="mt-1 h-1.5 w-1.5 rounded-full bg-primaryColor" />
              <span>{item}</span>
            </li>
          ))}
        </ul>

        <div className="mt-auto">
          <span className="inline-flex items-center justify-center w-full px-4 py-2 rounded-md bg-primaryColor text-white font-semibold text-sm hover:bg-primaryColor/90 transition-colors">
            {t('app:pricing.chooseCta', 'Choose {{plan}}', { plan: plan.title })}
          </span>
        </div>
      </div>
    </button>
  );
};

const PricingPage = () => {
  const history = useHistory();
  const brand = useBranding();
  const { t } = useTranslation('app');
  const pricingPlans = useMemo(() => buildPricingPlans(t), [t]);

  if (brand.key !== 'carbonglance') return <Redirect to="/" />;

  const handleSelectPlan = (plan) => {
    // Route into onboarding wizard with the relevant type
    if (plan.targetType === 'declarant') {
      history.push('/onboarding/wizard?type=declarant&source=pricing');
    } else if (plan.targetType === 'operator') {
      history.push('/onboarding/wizard?type=operator&source=pricing');
    }
  };

  return (
    <div className="min-h-screen w-full bg-gray-50 flex items-center justify-center p-6">
      <div className="w-full max-w-5xl">
        {/* Page Header - mirrors OnboardingTypeSelection visual style */}
        <div className="text-center mb-12">
          <img
            src={brand.logoUrl || logo}
            alt={t('app:pricing.logoAlt', '{{brandName}} logo', { brandName: brand.name })}
            className="h-12 mx-auto mb-6"
          />
          <h1 className="text-4xl font-bold text-gray-900 mb-3">
            {t('app:pricing.heading', 'Choose Your {{brandName}} Plan', { brandName: brand.name })}
          </h1>
          <p className="text-lg text-gray-600">
            {t(
              'app:pricing.subtitle',
              'Transparent pricing for declarants and operators. Select the plan that matches how you use CBAM.',
            )}
          </p>
        </div>

        {/* Pricing Plans */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {pricingPlans.map((plan) => (
            <PricingPlanCard
              key={plan.id}
              plan={plan}
              onSelect={handleSelectPlan}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default PricingPage;
